import React, { useEffect, useState } from "react";
import { Modal, Button, Form, message, Spin } from "antd";
import RecordingForm from "./Form";
import {
  useRecordingCreate,
  useRecordingUpdate,
  useRecordingGetById,
} from "../../../../../hooks/api/recordings";

interface RecordingModalProps {
  open: boolean;
  mode: "create" | "edit";
  onClose: () => void;
  onSave?: () => void;
  recordId?: string;
}

export default function RecordingModal({
  open,
  mode,
  onClose,
  onSave,
  recordId,
}: RecordingModalProps): React.ReactElement {
  const [form] = Form.useForm();
  const createRecording = useRecordingCreate();
  const updateRecording = useRecordingUpdate(recordId);
  const recordingById = useRecordingGetById();

  // Состояние для initialOptions тегов (id + name)
  const [tagOptions, setTagOptions] = useState<
    { label: string; value: string }[]
  >([]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    if (mode === "edit" && recordId && open) {
      recordingById.fetch(undefined, recordId);
    }
  }, [mode, recordId, open]);

  // Когда данные загрузились — формируем tagOptions из middahs
  useEffect(() => {
    if (recordingById.data?.middahs) {
      const options = recordingById.data.middahs.map((m: any) => ({
        label: m.name,
        value: m.id,
      }));
      setTagOptions(options);
    }
  }, [recordingById.data]);

  // Заполняем форму
  useEffect(() => {
    if (open && mode === "edit" && recordingById.data) {
      const record = recordingById.data;
      form.setFieldsValue({
        name: record.name,
        middahs: record.middahs?.map((m: any) => m.id) || [],
        globalContent: record.globalContent ?? false,
        type: record.type,
        fileLink: record.fileLink,
      });
      console.log(form.getFieldsValue());
    }
  }, [open, mode, recordingById.data, form]);

  // Сброс при создании
  useEffect(() => {
    if (open && mode === "create") {
      form.resetFields();
      setTagOptions([]);
    }
  }, [open, mode, form]);

  const handleSubmit = async (values: any) => {
    const payload = {
      name: values.name,
      type: values.type,
      fileLink: values.fileLink,
      middahs: values.middahs?.map((id: string) => ({ id })) || [],
      globalContent: values.globalContent ?? false,
    };

    try {
      if (mode === "edit") {
        await updateRecording.fetch(payload, recordId);
        message.success("Recording updated successfully");
      } else {
        await createRecording.fetch(payload);
        message.success("Recording uploaded successfully");
      }
      onSave?.();
      handleClose();
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const isLoading = mode === "edit" && recordingById.loading;

  const footer = [
    <Button key="cancel" onClick={handleClose}>
      Cancel
    </Button>,
    <Button
      key="save"
      type="primary"
      onClick={() => form.submit()}
      loading={
        isLoading ||
        (mode === "edit" ? updateRecording.loading : createRecording.loading)
      }
    >
      Save
    </Button>,
  ];

  return (
    <Modal
      title={mode === "edit" ? "Edit Recording" : "Add Recording"}
      open={open}
      onCancel={handleClose}
      width={520}
      footer={footer}
      destroyOnClose
    >
      {isLoading ? (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin />
        </div>
      ) : (
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <RecordingForm mode={mode} initialTagOptions={tagOptions} />
        </Form>
      )}
    </Modal>
  );
}
